'use client'

import { twMerge } from '@/lib/classname/twMerge'
import type { WebViewIntent } from '@/lib/react-native/services/react-native.intent'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import type { TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Picture } from '@/ui/media/picture/Picture.client'
import type { Image } from '@gql-generated/graphql'
import type React from 'react'
import { Button } from 'react-aria-components'
import { Link } from '../link/Link.client'

export const BLOCK_IMAGE_LINK_MODE = 'contain'
export const BLOCK_IMAGE_LINK_CLASSNAME = 'max-h-7 w-fit max-w-full'
export const BLOCK_IMAGE_LINK_PICTURE_CLASSNAME = 'flex w-auto min-w-20 justify-center text-center'

export type BlockImageLinkProps = React.PropsWithChildren<
  UiComponentProps<{
    pictureClassName?: string

    /** The image sent to the block */
    image?: Image

    /** The image component sent to the block */
    imageComponent?: React.ReactNode

    /** When the block is pressed */
    onPress?: () => void

    /** Is the block active ? */
    isActive?: boolean

    /** The link of the block */
    href?: string

    /** Open the link in a new tab */
    opensInNewTab?: boolean

    /** The event to track */
    trackingData?: TrackingGaEvent

    intent?: WebViewIntent | undefined
  }>
>

export function BlockImageLink({
  className,
  href,
  image,
  imageComponent,
  children,
  onPress,
  pictureClassName,
  isActive = false,
  opensInNewTab,
  trackingData,
  intent,
  id,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: BlockImageLinkProps): React.ReactNode {
  const classes = twMerge(
    'flex h-15 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-sm border-none bg-white p-1 text-center text-sm !font-medium text-brand-2-900',
    !image && 'desktop:justify-self-center',
  )

  const handlePress = () => {
    handleIntent(intent, {
      whenInBrowser: () => {
        if (trackingData) {
          pushToGtmDataLayer(trackingData)
        }

        onPress?.()
      },
    })
  }

  const pictureNode =
    imageComponent ??
    (image && (
      <Picture
        image={image}
        mode={BLOCK_IMAGE_LINK_MODE}
        className={twMerge(BLOCK_IMAGE_LINK_CLASSNAME, className)}
        pictureClassName={twMerge(BLOCK_IMAGE_LINK_PICTURE_CLASSNAME, pictureClassName)}
      />
    ))

  if (!href) {
    return (
      <Button
        onPress={handlePress}
        preventFocusOnPress
        className={twMerge(classes, 'data-[hovered]:text-brand-1-800 data-[hovered]:underline')}
        id={id}
        data-testid={testId ?? 'block-image-button'}
        aria-label={ariaLabel}
      >
        {pictureNode}
        {children && (
          <span className={twMerge('line-clamp-2', isActive && 'text-brand-1-700 underline')}>
            {children}
          </span>
        )}
      </Button>
    )
  }

  return (
    <Link
      variant="free"
      underlined="hover"
      href={href}
      className={classes}
      opensInNewTab={opensInNewTab}
      trackingData={trackingData}
      intent={intent}
      id={id}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {pictureNode}
      {children && (
        <span className={twMerge('line-clamp-2', isActive && 'text-brand-1-700 underline')}>
          {children}
        </span>
      )}
    </Link>
  )
}
